import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import ConfirmModal from "./ConfirmModal";
import { Course, Player, ScoreMap } from "../types";
import {
  GroupScores,
  fetchTournamentScores,
  submitGroupScores,
} from "../tournamentPlay";
import {
  ActiveGame,
  BingoSelections,
  OoslerSelections,
  WolfSelections,
} from "../activeRound";
import { loadOwner } from "../roster";
import {
  StablefordOptions,
  runStableford,
} from "../runners/stableford";
import {
  StrokePlayOptions,
  TeamSetup,
  runStrokePlay,
  teamsFromSetup,
} from "../runners/strokePlay";
import { SkinsOptions, runSkins } from "../runners/skins";
import { AllianceOptions, runAlliance } from "../runners/alliance";
import { ChaChaChaOptions, runChaChaCha } from "../runners/chaChaCha";
import {
  StablefordTeamOptions,
  runStablefordTeam,
} from "../runners/stablefordTeam";
import { TeamBetOptions, settleTeamGame } from "../runners/teamBets";
import {
  OoslerWooslerOptions,
  runOoslerWoosler,
} from "../runners/ooslerWoosler";
import { PuttingOptions, runPutting } from "../runners/putting";
import { NinesOptions, runNines } from "../runners/nines";
import { WolfOptions, runWolf } from "../runners/wolf";
import { RabbitOptions, runRabbit } from "../runners/rabbit";
import {
  BbbCategory,
  BingoBangoBongoOptions,
  runBingoBangoBongo,
} from "../runners/bingoBangoBongo";
import { MatchPlayOptions, runMatchPlay } from "../runners/matchPlay";
import {
  BounceBackCategory,
  BounceBackOptions,
  runBounceBack,
} from "../runners/bounceBack";
import { HiLoOptions, runHiLo } from "../runners/hiLo";
import { VegasOptions, runVegas } from "../runners/vegas";
import {
  AcesAndDeucesOptions,
  runAcesAndDeuces,
} from "../runners/acesAndDeuces";
import {
  HollywoodOptions,
  runHollywood,
  shuffleRoundRobinPairings,
} from "../runners/hollywood";

interface Props {
  players: Player[];
  scores: ScoreMap;
  totalPar: number;
  // Optional — provide course + games when displaying for an active or
  // past round so per-game runners (Stableford for now) can compute and
  // surface their points alongside gross totals. Both must be present
  // for a runner to fire; missing either falls back to gross-only.
  course?: Course;
  games?: ActiveGame[];
  // When present (tournament rounds), the modal shows a Local | Tournament
  // toggle; "Tournament" scores these games instead of the local side games.
  tournamentGames?: ActiveGame[];
  // Tournament + this player's group. On the Tournament tab the modal submits
  // this group's scores and fetches every group's, computing the standings
  // across the whole field.
  tournamentId?: string;
  tournamentGroupId?: string;
  tournamentGroupName?: string;
  // Per-hole Oosler/Woosler selections captured live during the round.
  // Optional — only present when the Oosler game has been enabled.
  ooslerSelections?: OoslerSelections;
  // Per-hole Wolf decisions captured live during the round. Optional — only
  // present when the Wolf game has been enabled.
  wolfSelections?: WolfSelections;
  // Per-hole Bingo Bango Bongo winners. Optional — only present when BBB
  // has been enabled.
  bingoSelections?: BingoSelections;
  // Live games-config setter. Optional — when omitted, leaderboard
  // affordances that mutate game options (e.g. Hollywood's Shuffle
  // button) hide themselves. Present in scorecard rounds, absent in
  // read-only history rounds.
  onGamesChange?: (next: ActiveGame[]) => void;
  // Optional. When omitted (e.g. when the modal is opened from History
  // to view a past round), the End Round affordance doesn't render —
  // history rounds aren't editable.
  onEndRound?: () => void;
  subtitle?: string;
  // Modal heading. Defaults to "Leaderboard"; the Tournament tab passes
  // "Tournament" to reuse this modal for the tournament-games view.
  title?: string;
  // Tournament-results mode (admin View Scores / player History for a
  // tournament round): force the Tournament board and hide the
  // My Group | Tournament toggle — there's no "my group" to compare against.
  tournamentOnly?: boolean;
  // Pre-fetched cross-group scores. When provided, the Tournament board is
  // seeded so it renders fully on open (no loading flash) — the caller has
  // already loaded them (e.g. behind a spinner on the View Scores button).
  initialFieldGroups?: GroupScores[];
  onViewScorecard?: () => void;
  onClose: () => void;
}

interface Row {
  player: Player;
  total: number;
  frontGross: number;
  backGross: number;
  holesPlayed: number;
  stablefordTotal: number | null;
  frontStbl: number | null;
  backStbl: number | null;
}

export default function LeaderboardModal({
  players: localPlayers,
  scores: localScores,
  totalPar: _totalPar,
  course,
  games: localGames,
  tournamentGames,
  tournamentId,
  tournamentGroupId,
  tournamentGroupName,
  ooslerSelections,
  wolfSelections,
  bingoSelections,
  onGamesChange,
  onEndRound,
  subtitle,
  title = "Leaderboard",
  tournamentOnly = false,
  initialFieldGroups,
  onViewScorecard,
  onClose,
}: Props) {
  // Tournament rounds get a Local | Tournament toggle. "Local" scores the
  // player's own group's side games; "Tournament" scores the tournament's
  // games across EVERY group's submitted scores. Everything below reads
  // `games` / `players` / `scores`, so switching the tab re-runs every board.
  // In tournamentOnly mode there's no local group, so we start on (and stay
  // on) the Tournament board and hide the toggle.
  const hasTournament = !!tournamentGames && tournamentGames.length > 0;
  const [board, setBoard] = useState<"local" | "tournament">(
    tournamentOnly ? "tournament" : "local",
  );

  // All groups' submitted players + scores, fetched when the Tournament tab is
  // opened. We push our own group first so the fetch reflects our latest.
  const [fieldGroups, setFieldGroups] = useState<GroupScores[]>(
    initialFieldGroups ?? [],
  );
  // Tracks the cross-group fetch so the UI can keep the My Group board on
  // screen with a spinner until the first tournament fetch lands, rather than
  // flashing an empty board. `loadingTournament` is true while a fetch is in
  // flight; `hasLoadedTournament` flips true after the first one completes so
  // subsequent refreshes don't drop back to the local view. Seeded true when
  // the caller pre-fetched (initialFieldGroups) so the board opens fully loaded.
  const [_loadingTournament, setLoadingTournament] = useState(false);
  const [hasLoadedTournament, setHasLoadedTournament] = useState(
    !!initialFieldGroups,
  );
  useEffect(() => {
    if (board !== "tournament" || !hasTournament || !tournamentId) return;
    let alive = true;
    setLoadingTournament(true);
    void (async () => {
      if (tournamentGroupId) {
        await submitGroupScores(
          tournamentId,
          tournamentGroupId,
          tournamentGroupName ?? "",
          localPlayers,
          localScores,
        );
      }
      const groups = await fetchTournamentScores(tournamentId);
      if (alive) {
        setFieldGroups(groups);
        setHasLoadedTournament(true);
        setLoadingTournament(false);
      }
    })();
    return () => {
      alive = false;
    };
    // localPlayers/localScores intentionally excluded — captured at open; the
    // modal sits over the scorecard so they don't change while it's open.
  }, [board, hasTournament, tournamentId, tournamentGroupId]);

  // Merge every group into one field for the tournament board.
  const fieldPlayers = useMemo(
    () => fieldGroups.flatMap((g) => g.players),
    [fieldGroups],
  );
  const fieldScores = useMemo(() => {
    const m: ScoreMap = {};
    for (const g of fieldGroups) Object.assign(m, g.scores);
    return m;
  }, [fieldGroups]);

  // Only switch the rendered data to the tournament field once it's actually
  // loaded — until then we keep showing the My Group board (with a spinner)
  // so the modal doesn't collapse to bare headers mid-fetch.
  const onTournamentBoard =
    board === "tournament" && hasTournament && hasLoadedTournament;
  const games = onTournamentBoard ? tournamentGames : localGames;
  const players = onTournamentBoard ? fieldPlayers : localPlayers;
  const scores = onTournamentBoard ? fieldScores : localScores;

  // +/- colour class for a money amount (shared by the team boards).
  const moneyClass = (n: number) =>
    n > 0 ? "cg-winnings-pos" : n < 0 ? "cg-winnings-neg" : "cg-winnings-zero";
  // Run Stableford if it's enabled for this round and we have the
  // course data the runner needs. Memoised so re-renders don't redo
  // the per-hole math. We also keep the resolved options so the
  // Stableford betting sub-board can label which variant is being
  // played (Net/Gross + scale).
  const stableford = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "stableford" && g.enabled,
    );
    if (!active || !course) return null;
    const opts = active.options as unknown as StablefordOptions;
    return {
      ...runStableford({
        course,
        players,
        scores,
        options: opts,
      }),
      options: opts,
    };
  }, [games, course, players, scores]);

  // Putting. Runs only when the game is enabled and the round has a
  // course. Panel renders when any bet is configured (bet-per-point or
  // any of the three Nassau pots); the layout switches to a Nassau
  // OUT / IN / TOTAL grid when nassau is ticked, otherwise it's a
  // single TOTAL column.
  const putting = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "putting" && g.enabled,
    );
    if (!active || !course) return null;
    const res = runPutting({
      course,
      players,
      scores,
      options: active.options as unknown as PuttingOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Alliance (Bowmaker) — TEAM game. Each group is a team; ranks groups by
  // combined best-N net (or gross) score. Only on the Tournament board, where
  // we have every group's scores (fieldGroups).
  const alliance = useMemo(() => {
    if (!onTournamentBoard || !course) return null;
    const active = games?.find((g) => g.id === "alliance" && g.enabled);
    if (!active) return null;
    const res = runAlliance({
      course,
      groups: fieldGroups,
      options: active.options as unknown as AllianceOptions,
    });
    return res.teams.some((t) => t.holesPlayed > 0) ? res : null;
  }, [onTournamentBoard, games, course, fieldGroups]);

  // Cha Cha Cha — TEAM game; ranks groups by combined best-N net where N
  // rotates 1→2→3 every three holes (with optional Lone Ranger). Tournament
  // board only.
  const chaChaCha = useMemo(() => {
    if (!onTournamentBoard || !course) return null;
    const active = games?.find((g) => g.id === "chaChaCha" && g.enabled);
    if (!active) return null;
    const res = runChaChaCha({
      course,
      groups: fieldGroups,
      options: active.options as unknown as ChaChaChaOptions,
    });
    return res.teams.some((t) => t.holesPlayed > 0) ? res : null;
  }, [onTournamentBoard, games, course, fieldGroups]);

  // Stableford best-net — TEAM variant. Ranks groups by combined best-N net
  // Stableford points (highest wins). Replaces the individual Stableford board
  // on the Tournament tab.
  const stablefordTeam = useMemo(() => {
    if (!onTournamentBoard || !course) return null;
    const active = games?.find((g) => g.id === "stableford" && g.enabled);
    if (!active) return null;
    const res = runStablefordTeam({
      course,
      groups: fieldGroups,
      options: active.options as unknown as StablefordTeamOptions,
    });
    return res.teams.some((t) => t.holesPlayed > 0) ? res : null;
  }, [onTournamentBoard, games, course, fieldGroups]);

  // Team-game betting payouts (pot / per-point / Nassau + entry-fee-paid),
  // settled across the field. Alliance & Cha Cha Cha are lower-wins; Stableford
  // is higher-wins.
  const allianceBets = useMemo(() => {
    if (!alliance) return null;
    const active = games?.find((g) => g.id === "alliance" && g.enabled);
    if (!active) return null;
    const res = settleTeamGame(
      alliance.teams,
      active.options as unknown as TeamBetOptions,
      true,
    );
    return res.hasAnyBet ? res : null;
  }, [alliance, games]);

  const chaChaChaBets = useMemo(() => {
    if (!chaChaCha) return null;
    const active = games?.find((g) => g.id === "chaChaCha" && g.enabled);
    if (!active) return null;
    const res = settleTeamGame(
      chaChaCha.teams,
      active.options as unknown as TeamBetOptions,
      true,
    );
    return res.hasAnyBet ? res : null;
  }, [chaChaCha, games]);

  const stablefordTeamBets = useMemo(() => {
    if (!stablefordTeam) return null;
    const active = games?.find((g) => g.id === "stableford" && g.enabled);
    if (!active) return null;
    const res = settleTeamGame(
      stablefordTeam.teams,
      active.options as unknown as TeamBetOptions,
      false,
    );
    return res.hasAnyBet ? res : null;
  }, [stablefordTeam, games]);

  // Oosler / Woosler. Runs only when the game is enabled and the round
  // has a course. The runner is safe to call with an empty selections
  // map (returns zeros for everyone), but the panel only shows once
  // SOME Par-3 has been touched — either settled with movement OR
  // sitting in carry — so an untouched round doesn't add visual noise.
  const oosler = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "ooslerWoosler" && g.enabled,
    );
    if (!active || !course) return null;
    const res = runOoslerWoosler({
      course,
      players,
      scores,
      selections: ooslerSelections || {},
      options: active.options as unknown as OoslerWooslerOptions,
    });
    const anyMovement = Object.values(res.payouts).some(
      (p) => p.points !== 0,
    );
    const anyCarry = Object.keys(res.carries).length > 0;
    return anyMovement || anyCarry ? res : null;
  }, [games, course, players, scores, ooslerSelections]);

  // Skins. Supports both pot (single round-long ante, valuePerSkin =
  // pot ÷ skinsWon) and carry-forward (per-hole stake that rolls on
  // ties). The runner picks the settlement based on opts.mode and
  // returns the same SkinsResult shape for either path; the panel
  // below renders identically — only the title and the optional
  // carry-forward "unclaimed" footnote differ.
  const skins = useMemo(() => {
    const active = games?.find((g) => g.id === "skins" && g.enabled);
    if (!active || !course) return null;
    const opts = active.options as unknown as SkinsOptions;
    const res = runSkins({ course, players, scores, options: opts });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Per-player Skins stake, subtracted from gross winnings to get net position.
  // Zero when "Entry Fee Paid" is on — the pot was collected up front, so
  // winners show the full pot and non-winners $0 (no buy-in deducted).
  const skinsStake = useMemo(() => {
    if (!skins) return 0;
    const active = games?.find((g) => g.id === "skins" && g.enabled);
    if (active?.options?.entryFeePaid === true) return 0;
    const n = players.length || 1;
    return (skins.gross.totalPot + skins.net.totalPot) / n;
  }, [skins, games, players.length]);

  // Nines. 3-ball only — the catalog gate keeps it out of other player
  // counts, but the runner is defensive and returns an empty result
  // otherwise. Panel renders when any bet is configured.
  const nines = useMemo(() => {
    const active = games?.find((g) => g.id === "nines" && g.enabled);
    if (!active || !course) return null;
    const res = runNines({
      course,
      players,
      scores,
      options: active.options as unknown as NinesOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Wolf. 4-ball only — settlement reads the live per-hole decisions
  // (wolfSelections). Panel renders once any hole has settled. scoringMode is
  // carried alongside the result so the board title can label Gross/Net.
  const wolf = useMemo(() => {
    const active = games?.find((g) => g.id === "wolf" && g.enabled);
    if (!active || !course) return null;
    const opts = active.options as unknown as WolfOptions;
    const res = runWolf({
      course,
      players,
      scores,
      selections: wolfSelections || {},
      options: opts,
    });
    const anyMovement = Object.values(res.payouts).some(
      (p) => p.details.length > 0,
    );
    return res.hasAnyBet && anyMovement
      ? { ...res, scoringMode: opts.scoringMode === "net" ? "net" : "gross" }
      : null;
  }, [games, course, players, scores, wolfSelections]);

  // Rabbit. Ownership is computed from scores (no per-hole picker). Panel
  // shows once a bet is set and the Rabbit has been captured or a pot settled.
  const rabbit = useMemo(() => {
    const active = games?.find((g) => g.id === "rabbit" && g.enabled);
    if (!active || !course) return null;
    const opts = active.options as unknown as RabbitOptions;
    const res = runRabbit({ course, players, scores, options: opts });
    const anyActivity =
      res.frontWinner !== null ||
      res.backWinner !== null ||
      res.frontSettled ||
      res.backSettled;
    return res.hasAnyBet && anyActivity
      ? { ...res, scoringMode: opts.scoringMode === "net" ? "net" : "gross" }
      : null;
  }, [games, course, players, scores]);

  // Bingo Bango Bongo. Driven entirely by the live per-hole winner picks.
  // Panel shows once a bet is set and at least one hole has been decided.
  const bbb = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "bingoBangoBongo" && g.enabled,
    );
    if (!active) return null;
    const res = runBingoBangoBongo({
      players,
      selections: bingoSelections || {},
      options: active.options as unknown as BingoBangoBongoOptions,
    });
    const anyDecided = Object.values(res.payouts).some(
      (p) =>
        p.bingo.count > 0 || p.bango.count > 0 || p.bongo.count > 0,
    );
    return res.hasAnyBet && anyDecided ? res : null;
  }, [games, players, bingoSelections]);

  // Bounce Back. Gross-only, derived straight from the scorecard. Panel
  // shows once a bet is set and at least one bounce-back event has fired.
  const bounceBack = useMemo(() => {
    const active = games?.find((g) => g.id === "bounceBack" && g.enabled);
    if (!active || !course) return null;
    const res = runBounceBack({
      course,
      players,
      scores,
      options: active.options as unknown as BounceBackOptions,
    });
    const anyEvent = Object.values(res.payouts).some(
      (p) => p.bbPar.count > 0 || p.bbBird.count > 0 || p.bbDbl.count > 0,
    );
    return res.hasAnyBet && anyEvent ? res : null;
  }, [games, course, players, scores]);

  // Match Play. 1-v-1 (two players) or 2-v-2 (four + Team). Panel shows once
  // the match is a valid shape, a bet is set, and a hole has been decided.
  const matchPlay = useMemo(() => {
    const active = games?.find((g) => g.id === "matchPlay" && g.enabled);
    if (!active || !course) return null;
    const res = runMatchPlay({
      course,
      players,
      scores,
      options: active.options as unknown as MatchPlayOptions,
    });
    const anyThru = Object.values(res.payouts).some((p) => p.thru > 0);
    return res.active && res.hasAnyBet && anyThru ? res : null;
  }, [games, course, players, scores]);

  // Hi-Lo. 4-ball only — catalog gate enforces minPlayers/maxPlayers,
  // and the runner returns an empty result otherwise. Panel renders
  // when the bet-per-point is configured.
  const hiLo = useMemo(() => {
    const active = games?.find((g) => g.id === "hiLo" && g.enabled);
    if (!active || !course) return null;
    const res = runHiLo({
      course,
      players,
      scores,
      options: active.options as unknown as HiLoOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Vegas. 4-ball, points-only — runner returns per-team aggregate
  // differential and per-hole detail rows. Panel renders when the
  // bet-per-point rate is configured.
  const vegas = useMemo(() => {
    const active = games?.find((g) => g.id === "vegas" && g.enabled);
    if (!active || !course) return null;
    const res = runVegas({
      course,
      players,
      scores,
      options: active.options as unknown as VegasOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Aces & Deuces. Per-hole side bet — outright low takes 1 from each
  // opponent, outright high pays 1 to each opponent, ties produce no
  // movement. Runs whenever the game is enabled and at least one bet
  // input has a value (either pointBet or any of the three Nassau pots).
  const acesAndDeuces = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "acesAndDeuces" && g.enabled,
    );
    if (!active || !course) return null;
    const res = runAcesAndDeuces({
      course,
      players,
      scores,
      options: active.options as unknown as AcesAndDeucesOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, course, players, scores]);

  // Hollywood (Six-Six-Six). 4-ball only — gated by the catalog's
  // min/maxPlayers, with a defensive empty result in the runner if
  // somehow called with a different count. Three 6-hole segments;
  // each segment carries its own pairing surfaced via the catalog
  // options (pairing1/2/3). Panel always renders the pairings even
  // when the bet is unset so the user can see the rotation; betting
  // settles segment-by-segment.
  const hollywood = useMemo(() => {
    const active = games?.find((g) => g.id === "hollywood" && g.enabled);
    if (!active || !course) return null;
    return runHollywood({
      course,
      players,
      scores,
      options: active.options as unknown as HollywoodOptions,
    });
  }, [games, course, players, scores]);

  // Shuffle the three Hollywood pairings into a fresh round-robin order
  // and write the result back into the round's games array. Hidden
  // when onGamesChange isn't wired (read-only history rounds) and
  // when the Hollywood game isn't enabled. Each call lands on one of
  // the six fair permutations.
  const _shuffleHollywoodPairings = () => {
    if (!onGamesChange || !games) return;
    const idx = games.findIndex((g) => g.id === "hollywood" && g.enabled);
    if (idx === -1) return;
    const [p1, p2, p3] = shuffleRoundRobinPairings();
    const next = games.slice();
    next[idx] = {
      ...next[idx],
      options: {
        ...next[idx].options,
        pairing1: p1,
        pairing2: p2,
        pairing3: p3,
      },
    };
    onGamesChange(next);
  };

  // Stroke Play is mandatory on every round but only emits a betting
  // board when at least one bet (overall or Nassau sub-bet) is configured.
  // Course isn't required — the runner only needs raw gross scores.
  const strokePlay = useMemo(() => {
    const active = games?.find(
      (g) => g.id === "strokePlay" && g.enabled,
    );
    if (!active) return null;
    const res = runStrokePlay({
      players,
      scores,
      options: active.options as unknown as StrokePlayOptions,
    });
    return res.hasAnyBet ? res : null;
  }, [games, players, scores]);

  // Per-game team-colour maps. Each map is derived independently from
  // its own game's `team` flag + `teamSetup`, so Stroke Play can run
  // individually while Stableford runs as teams (or vice versa) and
  // each sub-board's dots reflect only its own pairing. Returns null
  // when team play isn't active for that game OR the round doesn't
  // have exactly four players to pair.
  const buildTeamMap = (
    gameId: "strokePlay" | "stableford",
  ): Record<string, "team1" | "team2"> | null => {
    if (players.length !== 4) return null;
    const active = games?.find((g) => g.id === gameId && g.enabled);
    if (!active || active.options?.team !== true) return null;
    const setup = active.options.teamSetup as TeamSetup | undefined;
    const [team1, team2] = teamsFromSetup(players, setup);
    const map: Record<string, "team1" | "team2"> = {};
    for (const p of team1) map[p.id] = "team1";
    for (const p of team2) map[p.id] = "team2";
    return map;
  };

  // Hi-Lo is always team-based (no on/off Team checkbox in the catalog),
  // so its team map is built directly from the teamSetup option whenever
  // the game is enabled and the round has exactly four players.
  const hiLoTeams = useMemo<Record<string, "team1" | "team2"> | null>(() => {
    if (players.length !== 4) return null;
    const active = games?.find((g) => g.id === "hiLo" && g.enabled);
    if (!active) return null;
    const setup = active.options?.teamSetup as TeamSetup | undefined;
    const [team1, team2] = teamsFromSetup(players, setup);
    const map: Record<string, "team1" | "team2"> = {};
    for (const p of team1) map[p.id] = "team1";
    for (const p of team2) map[p.id] = "team2";
    return map;
  }, [games, players]);

  // Vegas team map — same shape as Hi-Lo since Vegas is always team-
  // based with the same teamSetup dropdown.
  const vegasTeams = useMemo<Record<string, "team1" | "team2"> | null>(() => {
    if (players.length !== 4) return null;
    const active = games?.find((g) => g.id === "vegas" && g.enabled);
    if (!active) return null;
    const setup = active.options?.teamSetup as TeamSetup | undefined;
    const [team1, team2] = teamsFromSetup(players, setup);
    const map: Record<string, "team1" | "team2"> = {};
    for (const p of team1) map[p.id] = "team1";
    for (const p of team2) map[p.id] = "team2";
    return map;
  }, [games, players]);

  const strokePlayTeams = useMemo(
    () => buildTeamMap("strokePlay"),
    [games, players],
  );
  const stablefordTeams = useMemo(
    () => buildTeamMap("stableford"),
    [games, players],
  );

  // Owner-configured currency symbol used by every formatMoney call
  // below. Snapshotted at modal mount — the user can't change currency
  // while the leaderboard is open. Defaults to "$".
  const currency = useMemo(() => loadOwner()?.betCurrency || "$", []);

  // Helper rendered alongside each player name on the betting sub-
  // boards. Takes the per-game map so a sub-board only ever shows its
  // own team play. Returns null when the supplied map is null or the
  // player isn't in either team for that game.
  const teamDot = (
    playerId: string,
    teamMap: Record<string, "team1" | "team2"> | null,
  ) => {
    if (!teamMap) return null;
    const t = teamMap[playerId];
    if (!t) return null;
    return (
      <span
        className={`cg-board-team-dot cg-board-team-dot-${t}`}
        aria-hidden
      />
    );
  };

  // Trophy emoji rendered after a player name when they're the leader
  // of the board it appears on. Ties surface a trophy on every tied
  // player. Returns null when `won` is false so callers can render
  // unconditionally.
  const trophy = (won: boolean) =>
    won ? (
      <span
        className="cg-board-trophy"
        role="img"
        aria-label="Leader"
      >
        🏆
      </span>
    ) : null;

  // Render order matches the players array — i.e. the scorecard order
  // (P1, P2, P3, P4). We intentionally do NOT sort by score: keeping
  // each player in their fixed slot makes it easier to scan during a
  // round and avoids rows jumping around as scores come in. Leader is
  // surfaced via the trophy icon instead. Players with zero recorded
  // holes are filtered out so an empty round doesn't render four
  // dashes; once anyone has a score the slot is preserved.
  const rows: Row[] = players
    .map((p) => {
      const ps = scores[p.id] || {};
      let frontGross = 0;
      let backGross = 0;
      let holesPlayed = 0;
      Object.entries(ps).forEach(([holeStr, sc]) => {
        if (typeof sc.gross !== "number") return;
        const h = parseInt(holeStr, 10);
        if (h >= 1 && h <= 9) frontGross += sc.gross;
        else if (h >= 10 && h <= 18) backGross += sc.gross;
        holesPlayed++;
      });
      let frontStbl: number | null = null;
      let backStbl: number | null = null;
      if (stableford) {
        frontStbl = 0;
        backStbl = 0;
        Object.entries(stableford.points[p.id] || {}).forEach(
          ([holeStr, pts]) => {
            const h = parseInt(holeStr, 10);
            if (h >= 1 && h <= 9) frontStbl! += pts;
            else if (h >= 10 && h <= 18) backStbl! += pts;
          },
        );
      }
      return {
        player: p,
        total: frontGross + backGross,
        frontGross,
        backGross,
        holesPlayed,
        stablefordTotal: stableford ? stableford.totals[p.id] || 0 : null,
        frontStbl,
        backStbl,
      };
    })
    // On the Tournament board keep the WHOLE field so every entrant's name
    // shows from the off (e.g. all Skins players before anyone tees off).
    // Elsewhere (a single group's own board) only show players who've started.
    .filter((r) => onTournamentBoard || r.holesPlayed > 0);

  // Leader sets for each board. Computed independently so the main
  // leaderboard's trophy reflects scoring leader(s) while each betting
  // board's trophy reflects the bet winner(s) — which for a team game
  // surfaces on both teammates together. Tied first-place values
  // produce multiple trophies. Empty sets render no trophies.
  const mainLeaders = useMemo<Set<string>>(() => {
    // Only players who've actually started contend for the trophy — on the
    // Tournament board the field includes not-yet-started entrants (total 0)
    // who must not be crowned leader.
    const started = rows.filter((r) => r.holesPlayed > 0);
    if (started.length === 0) return new Set();
    // Trophy on the main leaderboard always tracks lowest gross. Many
    // golfers in our user base play without a registered handicap, so
    // Stableford points aren't a reliable "winner" signal at the top
    // of the board. Stableford-based winners still surface on the
    // Stableford betting sub-board below.
    const min = Math.min(...started.map((r) => r.total));
    return new Set(
      started.filter((r) => r.total === min).map((r) => r.player.id),
    );
  }, [rows]);

  const strokePlayWinners = useMemo<Set<string>>(() => {
    if (!strokePlay || rows.length === 0) return new Set();
    const totals = rows.map(
      (r) => strokePlay.payouts[r.player.id]?.total || 0,
    );
    const max = Math.max(...totals);
    if (max <= 0) return new Set(); // nobody up money yet
    return new Set(
      rows
        .filter((r) => (strokePlay.payouts[r.player.id]?.total || 0) === max)
        .map((r) => r.player.id),
    );
  }, [strokePlay, rows]);

  const stablefordWinners = useMemo<Set<string>>(() => {
    if (!stableford?.hasAnyBet || rows.length === 0) return new Set();
    const totals = rows.map(
      (r) => stableford.payouts[r.player.id]?.total || 0,
    );
    const max = Math.max(...totals);
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter(
          (r) => (stableford.payouts[r.player.id]?.total || 0) === max,
        )
        .map((r) => r.player.id),
    );
  }, [stableford, rows]);

  // Combined Skins winner(s): max of (gross net + net net), where each
  // section's net is winnings − per-player stake. Trophy follows the
  // top net position rather than raw winnings so a player who took two
  // small skins but anted into a big pot doesn't out-trophy someone
  // sitting on a higher net.
  const skinsWinners = useMemo<Set<string>>(() => {
    if (!skins?.hasAnyBet || rows.length === 0) return new Set();
    const netFor = (id: string) =>
      (skins.gross.payouts[id]?.winnings || 0) +
      (skins.net.payouts[id]?.winnings || 0) -
      skinsStake;
    const max = Math.max(...rows.map((r) => netFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => netFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [skins, rows, skinsStake]);

  // Highest-net Oosler/Woosler player(s) — trophy honours the leader
  // among positive movers; everyone tied at the max gets a trophy.
  // Empty set when no settlement has happened yet or the leader is at
  // or below zero (rare but possible in heavy-Woosler rounds).
  const ooslerWinners = useMemo<Set<string>>(() => {
    if (!oosler || rows.length === 0) return new Set();
    const totals = rows.map((r) => oosler.payouts[r.player.id]?.points || 0);
    const max = Math.max(...totals);
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => (oosler.payouts[r.player.id]?.points || 0) === max)
        .map((r) => r.player.id),
    );
  }, [oosler, rows]);

  const _hollywoodWinners = useMemo<Set<string>>(() => {
    if (!hollywood || !hollywood.hasAnyBet || rows.length === 0) {
      return new Set();
    }
    const max = Math.max(
      ...rows.map((r) => hollywood.payouts[r.player.id]?.total || 0),
    );
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => (hollywood.payouts[r.player.id]?.total || 0) === max)
        .map((r) => r.player.id),
    );
  }, [hollywood, rows]);

  const vegasWinners = useMemo<Set<string>>(() => {
    if (!vegas || rows.length === 0) return new Set();
    const max = Math.max(
      ...rows.map((r) => vegas.payouts[r.player.id]?.total || 0),
    );
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => (vegas.payouts[r.player.id]?.total || 0) === max)
        .map((r) => r.player.id),
    );
  }, [vegas, rows]);

  const hiLoWinners = useMemo<Set<string>>(() => {
    if (!hiLo || rows.length === 0) return new Set();
    // Trophy on both members of the winning team. Settles on the
    // player's total net (positive means their team won the match).
    const max = Math.max(
      ...rows.map((r) => hiLo.payouts[r.player.id]?.total || 0),
    );
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => (hiLo.payouts[r.player.id]?.total || 0) === max)
        .map((r) => r.player.id),
    );
  }, [hiLo, rows]);

  const ninesWinners = useMemo<Set<string>>(() => {
    if (!nines || rows.length === 0) return new Set();
    // Winner = highest combined net cash across gross + net sections.
    // In single-mode rounds one of the two sections sits at zeros.
    const totalFor = (id: string) =>
      (nines.gross.payouts[id]?.total || 0) +
      (nines.net.payouts[id]?.total || 0);
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => totalFor(r.player.id) === max)
        .map((r) => r.player.id),
    );
  }, [nines, rows]);

  const wolfWinners = useMemo<Set<string>>(() => {
    if (!wolf || rows.length === 0) return new Set();
    const totalFor = (id: string) => wolf.payouts[id]?.total || 0;
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => totalFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [wolf, rows]);

  const rabbitWinners = useMemo<Set<string>>(() => {
    if (!rabbit || rows.length === 0) return new Set();
    const totalFor = (id: string) => rabbit.payouts[id]?.total || 0;
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => totalFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [rabbit, rows]);

  const bbbWinners = useMemo<Set<string>>(() => {
    if (!bbb || rows.length === 0) return new Set();
    const totalFor = (id: string) => bbb.payouts[id]?.total || 0;
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => totalFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [bbb, rows]);

  const bounceBackWinners = useMemo<Set<string>>(() => {
    if (!bounceBack || rows.length === 0) return new Set();
    const totalFor = (id: string) => bounceBack.payouts[id]?.total || 0;
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => totalFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [bounceBack, rows]);

  const matchPlayWinners = useMemo<Set<string>>(() => {
    if (!matchPlay || rows.length === 0) return new Set();
    const totalFor = (id: string) => matchPlay.payouts[id]?.total || 0;
    const max = Math.max(...rows.map((r) => totalFor(r.player.id)));
    if (max <= 0) return new Set();
    return new Set(
      rows.filter((r) => totalFor(r.player.id) === max).map((r) => r.player.id),
    );
  }, [matchPlay, rows]);

  const acesWinners = useMemo<Set<string>>(() => {
    if (!acesAndDeuces || rows.length === 0) return new Set();
    const totals = rows.map(
      (r) => acesAndDeuces.payouts[r.player.id]?.total || 0,
    );
    const max = Math.max(...totals);
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter(
          (r) => (acesAndDeuces.payouts[r.player.id]?.total || 0) === max,
        )
        .map((r) => r.player.id),
    );
  }, [acesAndDeuces, rows]);

  const puttingWinners = useMemo<Set<string>>(() => {
    if (!putting || rows.length === 0) return new Set();
    // Trophy follows the money — highest net total wins. Empty when
    // nobody is up, so no trophy renders on a flat or losing-only round.
    const totals = rows.map(
      (r) => putting.payouts[r.player.id]?.total || 0,
    );
    const max = Math.max(...totals);
    if (max <= 0) return new Set();
    return new Set(
      rows
        .filter((r) => (putting.payouts[r.player.id]?.total || 0) === max)
        .map((r) => r.player.id),
    );
  }, [putting, rows]);

  // Click-to-expand state for the Putting category cells. Same pattern
  // as Skins: identifies the row + which of the four categories is
  // open. Re-tapping the same cell closes the detail row.
  const [openPuttingDetail, setOpenPuttingDetail] = useState<{
    playerId: string;
    category: "zeroPutts" | "onePutts" | "threePutts" | "uAndD";
  } | null>(null);

  const togglePuttingDetail = (
    playerId: string,
    category: "zeroPutts" | "onePutts" | "threePutts" | "uAndD",
  ) => {
    setOpenPuttingDetail((prev) =>
      prev && prev.playerId === playerId && prev.category === category
        ? null
        : { playerId, category },
    );
  };

  // Click-to-expand state for the Skins detail (which holes a player
  // won a skin on). Identifies the row by playerId + section so the
  // Gross and Net columns each have their own toggle.
  const [openSkinDetail, setOpenSkinDetail] = useState<{
    playerId: string;
    section: "gross" | "net";
  } | null>(null);

  const toggleSkinDetail = (playerId: string, section: "gross" | "net") => {
    setOpenSkinDetail((prev) =>
      prev && prev.playerId === playerId && prev.section === section
        ? null
        : { playerId, section },
    );
  };

  // Click-to-expand state for the Aces & Deuces category cells. Same
  // pattern as Putting — tap a category count to reveal the hole list,
  // tap the same cell again to collapse.
  const [openAcesDetail, setOpenAcesDetail] = useState<{
    playerId: string;
    category: "aces" | "deuces";
  } | null>(null);

  const toggleAcesDetail = (
    playerId: string,
    category: "aces" | "deuces",
  ) => {
    setOpenAcesDetail((prev) =>
      prev && prev.playerId === playerId && prev.category === category
        ? null
        : { playerId, category },
    );
  };

  // Click-to-expand state for Nines point counts. Identifies the row
  // by playerId + section so Gross and Net columns each toggle on
  // their own (matches the Skins pattern).
  const [openNinesDetail, setOpenNinesDetail] = useState<{
    playerId: string;
    section: "gross" | "net";
  } | null>(null);

  const toggleNinesDetail = (playerId: string, section: "gross" | "net") => {
    setOpenNinesDetail((prev) =>
      prev && prev.playerId === playerId && prev.section === section
        ? null
        : { playerId, section },
    );
  };

  // Click-to-expand state for the Round Total panel — tapping a player's
  // row reveals their per-game breakdown beneath. Single playerId at a
  // time so the panel stays compact.
  const [openTotalDetail, setOpenTotalDetail] = useState<string | null>(null);
  const toggleTotalDetail = (playerId: string) => {
    setOpenTotalDetail((prev) => (prev === playerId ? null : playerId));
  };

  // Click-to-expand state for the Wolf board — tapping a player's total
  // reveals their per-hole settlement rows.
  const [openWolfDetail, setOpenWolfDetail] = useState<string | null>(null);
  const toggleWolfDetail = (playerId: string) => {
    setOpenWolfDetail((prev) => (prev === playerId ? null : playerId));
  };

  // Click-to-expand state for the BBB board — identifies a row by playerId +
  // category so each Bingo / Bango / Bongo count toggles its own hole list.
  const [openBbbDetail, setOpenBbbDetail] = useState<{
    playerId: string;
    category: BbbCategory;
  } | null>(null);
  const toggleBbbDetail = (playerId: string, category: BbbCategory) => {
    setOpenBbbDetail((prev) =>
      prev && prev.playerId === playerId && prev.category === category
        ? null
        : { playerId, category },
    );
  };

  // Click-to-expand state for the Bounce Back board — same shape as BBB:
  // playerId + category so each BB Par / BB Bird / BB Dbl count toggles its
  // own hole list.
  const [openBounceBackDetail, setOpenBounceBackDetail] = useState<{
    playerId: string;
    category: BounceBackCategory;
  } | null>(null);
  const toggleBounceBackDetail = (
    playerId: string,
    category: BounceBackCategory,
  ) => {
    setOpenBounceBackDetail((prev) =>
      prev && prev.playerId === playerId && prev.category === category
        ? null
        : { playerId, category },
    );
  };

  const [confirmingEnd, setConfirmingEnd] = useState(false);

  return (
    <ModalShell title={title} subtitle={subtitle} onClose={onClose}>
      {hasTournament && !tournamentOnly && (
        <div className="cg-board-tabs">
          <button
            type="button"
            className={`cg-board-tab${board === "local" ? " cg-board-tab-active" : ""}`}
            onClick={() => setBoard("local")}
          >
            My Group
          </button>
          <button
            type="button"
            className={`cg-board-tab${board === "tournament" ? " cg-board-tab-active" : ""}`}
            onClick={() => setBoard("tournament")}
          >
            {board === "tournament" && !hasLoadedTournament && (
              <span className="cg-tab-spinner" aria-hidden />
            )}
            Tournament
          </button>
        </div>
      )}
      {/* View scorecard sits at the TOP so it's reachable without scrolling
          past the full leaderboard + game boards (the bottom was hard to hit
          on iOS thanks to the overscroll bounce). History-only — active
          rounds don't pass onViewScorecard. */}
      {onViewScorecard && (
        <div className="cg-board-secondary cg-board-secondary-top">
          <button
            type="button"
            className="cg-btn-link cg-board-view-card"
            onClick={onViewScorecard}
          >
            View scorecard →
          </button>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="cg-modal-note">No scores yet.</p>
      ) : (
        <ol className="cg-board">
          <li className="cg-board-head cg-board-head-stbl" aria-hidden>
            <span className="cg-board-head-name">
              <span>Name</span>
              <span className="cg-board-legend-gross">Gross</span>
              {stableford && (
                <span className="cg-board-legend-stbl">Stbl</span>
              )}
            </span>
            <span className="cg-board-section">OUT</span>
            <span className="cg-board-section">IN</span>
            <span className="cg-board-section">TOTAL</span>
          </li>
          {rows.map((r) => (
            <li
              key={r.player.id}
              className="cg-board-row cg-board-row-stbl"
            >
              <span className="cg-board-name">
                {r.player.name}
                {trophy(mainLeaders.has(r.player.id))}
              </span>
              <span className="cg-board-pair">
                <span className="cg-board-gross">{r.frontGross}</span>
                {stableford && (
                  <span className="cg-board-stbl">{r.frontStbl}</span>
                )}
              </span>
              <span className="cg-board-pair">
                <span className="cg-board-gross">{r.backGross}</span>
                {stableford && (
                  <span className="cg-board-stbl">{r.backStbl}</span>
                )}
              </span>
              <span className="cg-board-pair">
                <span className="cg-board-gross">{r.total}</span>
                {stableford && (
                  <span className="cg-board-stbl">{r.stablefordTotal}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}

      {strokePlay && rows.length > 0 && (
        <section className="cg-game-board cg-game-board-strokePlay">
          <h3 className="cg-game-board-title">Stroke Play</h3>
          <ol className="cg-board cg-game-board-list">
            {strokePlay.nassauActive && (
              <li
                className="cg-board-head cg-board-head-payout-nassau"
                aria-hidden
              >
                <span />
                <span className="cg-board-section">OUT</span>
                <span className="cg-board-section">IN</span>
                <span className="cg-board-section">TOTAL</span>
              </li>
            )}
            {rows.map((r) => {
                const pay = strokePlay.payouts[r.player.id] || {
                  overall: 0,
                  front: 0,
                  back: 0,
                  total: 0,
                };
                const clsFor = (n: number) =>
                  n > 0
                    ? "cg-winnings-pos"
                    : n < 0
                      ? "cg-winnings-neg"
                      : "cg-winnings-zero";
                if (strokePlay.nassauActive) {
                  return (
                    <li
                      key={r.player.id}
                      className="cg-board-row cg-board-row-payout-nassau"
                    >
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(strokePlayWinners.has(r.player.id))}
                        {teamDot(r.player.id, strokePlayTeams)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.front)}`}
                      >
                        {formatMoney(pay.front, currency)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.back)}`}
                      >
                        {formatMoney(pay.back, currency)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.total)}`}
                      >
                        {formatMoney(pay.total, currency)}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={r.player.id}
                    className="cg-board-row cg-board-row-payout"
                  >
                    <span className="cg-board-name">
                      {r.player.name}
                      {trophy(strokePlayWinners.has(r.player.id))}
                      {teamDot(r.player.id, strokePlayTeams)}
                    </span>
                    <span
                      className={`cg-board-winnings ${clsFor(pay.total)}`}
                    >
                      {formatMoney(pay.total, currency)}
                    </span>
                  </li>
                );
              })}
          </ol>
        </section>
      )}

      {stableford && stableford.hasAnyBet && rows.length > 0 && !onTournamentBoard && (
        <section className="cg-game-board cg-game-board-stableford">
          <h3 className="cg-game-board-title">
            {stablefordLabel(stableford.options)}
          </h3>
          <ol className="cg-board cg-game-board-list">
            {stableford.nassauActive && (
              <li
                className="cg-board-head cg-board-head-payout-nassau"
                aria-hidden
              >
                <span />
                <span className="cg-board-section">OUT</span>
                <span className="cg-board-section">IN</span>
                <span className="cg-board-section">TOTAL</span>
              </li>
            )}
            {rows.map((r) => {
                const pay = stableford.payouts[r.player.id] || {
                  overall: 0,
                  front: 0,
                  back: 0,
                  total: 0,
                };
                const clsFor = (n: number) =>
                  n > 0
                    ? "cg-winnings-pos"
                    : n < 0
                      ? "cg-winnings-neg"
                      : "cg-winnings-zero";
                if (stableford.nassauActive) {
                  return (
                    <li
                      key={r.player.id}
                      className="cg-board-row cg-board-row-payout-nassau"
                    >
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(stablefordWinners.has(r.player.id))}
                        {teamDot(r.player.id, stablefordTeams)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.front)}`}
                      >
                        {formatMoney(pay.front, currency)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.back)}`}
                      >
                        {formatMoney(pay.back, currency)}
                      </span>
                      <span
                        className={`cg-board-winnings ${clsFor(pay.total)}`}
                      >
                        {formatMoney(pay.total, currency)}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={r.player.id}
                    className="cg-board-row cg-board-row-payout"
                  >
                    <span className="cg-board-name">
                      {r.player.name}
                      {trophy(stablefordWinners.has(r.player.id))}
                      {teamDot(r.player.id, stablefordTeams)}
                    </span>
                    <span
                      className={`cg-board-winnings ${clsFor(pay.total)}`}
                    >
                      {formatMoney(pay.total, currency)}
                    </span>
                  </li>
                );
              })}
          </ol>
        </section>
      )}

      {skins?.hasAnyBet && rows.length > 0 && (
        <section className="cg-game-board cg-game-board-skins">
          <h3 className="cg-game-board-title">
            Skins {skins.mode === "carryForward" ? "— Carry Forward" : "— Pot"}
          </h3>
          <ol className="cg-board cg-game-board-list">
            <li
              className="cg-board-head cg-board-head-skins-combined"
              aria-hidden
            >
              <span />
              <span className="cg-board-section">GROSS</span>
              <span className="cg-board-section">NET</span>
              <span className="cg-board-section">TOTAL</span>
            </li>
            {rows.map((r) => {
              const g = skins.gross.payouts[r.player.id] || {
                skinsWon: 0,
                winnings: 0,
                holes: [],
              };
              const n = skins.net.payouts[r.player.id] || {
                skinsWon: 0,
                winnings: 0,
                holes: [],
              };
              // Net position = winnings − per-player stake (skinsStake is 0
              // when the entry fee was paid up front, so winners then show the
              // full pot and non-winners $0).
              const total = g.winnings + n.winnings - skinsStake;
              const totalCls =
                total > 0
                  ? "cg-winnings-pos"
                  : total < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";
              const open = openSkinDetail;
              const isDetailOpen =
                !!open && open.playerId === r.player.id;
              const detail = isDetailOpen
                ? open.section === "gross"
                  ? g
                  : n
                : null;
              return (
                <React.Fragment key={r.player.id}>
                  <li className="cg-board-row cg-board-row-skins-combined">
                    <span className="cg-board-name">
                      {r.player.name}
                      {trophy(skinsWinners.has(r.player.id))}
                    </span>
                    <span className="cg-board-skin-pair">
                      <button
                        type="button"
                        className="cg-board-skin-count"
                        onClick={() => toggleSkinDetail(r.player.id, "gross")}
                        aria-expanded={
                          isDetailOpen && open?.section === "gross"
                        }
                        aria-label={`${r.player.name} gross skins won`}
                      >
                        {g.skinsWon}
                      </button>
                      <span className="cg-board-skin-win">
                        {formatMoney(g.winnings, currency, false)}
                      </span>
                    </span>
                    <span className="cg-board-skin-pair">
                      <button
                        type="button"
                        className="cg-board-skin-count"
                        onClick={() => toggleSkinDetail(r.player.id, "net")}
                        aria-expanded={
                          isDetailOpen && open?.section === "net"
                        }
                        aria-label={`${r.player.name} net skins won`}
                      >
                        {n.skinsWon}
                      </button>
                      <span className="cg-board-skin-win">
                        {formatMoney(n.winnings, currency, false)}
                      </span>
                    </span>
                    <span className={`cg-board-skin-total ${totalCls}`}>
                      {formatMoney(total, currency)}
                    </span>
                  </li>
                  {detail && (
                    <li className="cg-board-skin-detail">
                      {detail.holes.length === 0
                        ? `No ${open?.section} skins yet.`
                        : `${
                            open?.section === "gross" ? "Gross" : "Net"
                          } skins on hole${
                            detail.holes.length === 1 ? "" : "s"
                          }: ${detail.holes.join(", ")}`}
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
          {skins.mode === "carryForward" &&
            (skins.gross.unclaimedPot > 0 || skins.net.unclaimedPot > 0) && (
              <p className="cg-board-skin-carry-note">
                {skins.gross.unclaimedPot > 0 &&
                  `Gross carry: ${formatMoney(
                    skins.gross.unclaimedPot,
                    currency,
                    false,
                  )}`}
                {skins.gross.unclaimedPot > 0 &&
                  skins.net.unclaimedPot > 0 &&
                  " · "}
                {skins.net.unclaimedPot > 0 &&
                  `Net carry: ${formatMoney(
                    skins.net.unclaimedPot,
                    currency,
                    false,
                  )}`}
              </p>
            )}
        </section>
      )}

      {alliance && (
        <section className="cg-game-board cg-game-board-alliance">
          <h3 className="cg-game-board-title">Alliance (Bowmaker)</h3>
          <ol className="cg-board cg-game-board-list">
            {alliance.teams.map((t, i) => (
              <li key={t.groupId} className="cg-board-row">
                <span className="cg-board-pos">{i + 1}</span>
                <span className="cg-board-name">
                  {t.groupName || "Group"}
                  {trophy(i === 0 && t.holesPlayed > 0)}
                </span>
                <span className="cg-board-thru">
                  {t.holesPlayed} {t.holesPlayed === 1 ? "hole" : "holes"}
                </span>
                <span className="cg-board-total">{t.overall}</span>
                {allianceBets && (
                  <span
                    className={`cg-board-winnings ${moneyClass(
                      allianceBets.teamPayouts[t.groupId] || 0,
                    )}`}
                  >
                    {formatMoney(
                      allianceBets.teamPayouts[t.groupId] || 0,
                      currency,
                    )}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {chaChaCha && (
        <section className="cg-game-board cg-game-board-chachacha">
          <h3 className="cg-game-board-title">Cha Cha Cha</h3>
          <ol className="cg-board cg-game-board-list">
            {chaChaCha.teams.map((t, i) => (
              <li key={t.groupId} className="cg-board-row">
                <span className="cg-board-pos">{i + 1}</span>
                <span className="cg-board-name">
                  {t.groupName || "Group"}
                  {trophy(i === 0 && t.holesPlayed > 0)}
                </span>
                <span className="cg-board-thru">
                  {t.holesPlayed} {t.holesPlayed === 1 ? "hole" : "holes"}
                </span>
                <span className="cg-board-total">{t.overall}</span>
                {chaChaChaBets && (
                  <span
                    className={`cg-board-winnings ${moneyClass(
                      chaChaChaBets.teamPayouts[t.groupId] || 0,
                    )}`}
                  >
                    {formatMoney(
                      chaChaChaBets.teamPayouts[t.groupId] || 0,
                      currency,
                    )}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {stablefordTeam && (
        <section className="cg-game-board cg-game-board-stableford-team">
          <h3 className="cg-game-board-title">Stableford</h3>
          <ol className="cg-board cg-game-board-list">
            {stablefordTeam.teams.map((t, i) => (
              <li key={t.groupId} className="cg-board-row">
                <span className="cg-board-pos">{i + 1}</span>
                <span className="cg-board-name">
                  {t.groupName || "Group"}
                  {trophy(i === 0 && t.holesPlayed > 0)}
                </span>
                <span className="cg-board-thru">
                  {t.holesPlayed} {t.holesPlayed === 1 ? "hole" : "holes"}
                </span>
                <span className="cg-board-total cg-board-stableford">
                  {t.overall}
                </span>
                {stablefordTeamBets && (
                  <span
                    className={`cg-board-winnings ${moneyClass(
                      stablefordTeamBets.teamPayouts[t.groupId] || 0,
                    )}`}
                  >
                    {formatMoney(
                      stablefordTeamBets.teamPayouts[t.groupId] || 0,
                      currency,
                    )}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {oosler && rows.length > 0 && oosler.par3Holes.length > 0 && (
        <section className="cg-game-board cg-game-board-oosler">
          <h3 className="cg-game-board-title">Oosler / Woosler</h3>
          <div
            className="cg-oosler-matrix"
            style={{
              gridTemplateColumns: `minmax(0, 1fr) repeat(${oosler.par3Holes.length}, 34px) 44px`,
            }}
          >
            {/* Header row: empty cell over name, then each Par-3 hole
                number, then a Tot column for the running total. */}
            <div className="cg-oosler-matrix-head" />
            {oosler.par3Holes.map((h) => (
              <div key={`hh-${h}`} className="cg-oosler-matrix-head">
                H{h}
              </div>
            ))}
            <div className="cg-oosler-matrix-head">Tot</div>

            {/* One row per player. Cells show per-hole delta (0 for
                None / unresolved holes); total cell shows the running
                net with trophy on the name when leading. */}
            {rows.map((r) => {
              const pay = oosler.payouts[r.player.id] || {
                points: 0,
                details: [],
              };
              const totalCls =
                pay.points > 0
                  ? "cg-winnings-pos"
                  : pay.points < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";
              return (
                <React.Fragment key={r.player.id}>
                  <div className="cg-oosler-matrix-name">
                    {r.player.name}
                    {trophy(ooslerWinners.has(r.player.id))}
                  </div>
                  {oosler.par3Holes.map((h) => {
                    const v = oosler.perHole[h]?.[r.player.id] ?? 0;
                    const cls =
                      v > 0
                        ? "cg-winnings-pos"
                        : v < 0
                          ? "cg-winnings-neg"
                          : "cg-winnings-zero";
                    return (
                      <div
                        key={`pv-${r.player.id}-${h}`}
                        className={`cg-oosler-matrix-cell ${cls}`}
                      >
                        {formatMoney(v, currency)}
                      </div>
                    );
                  })}
                  <div className={`cg-oosler-matrix-total ${totalCls}`}>
                    {formatMoney(pay.points, currency)}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </section>
      )}

      {acesAndDeuces && rows.length > 0 && (() => {
        const active = games?.find(
          (g) => g.id === "acesAndDeuces" && g.enabled,
        );
        const mode =
          active?.options?.scoringMode === "gross" ? "Gross" : "Net";
        return (
        <section className="cg-game-board cg-game-board-aces">
          <h3 className="cg-game-board-title">Aces & Deuces — {mode}</h3>
          <ol className="cg-board cg-game-board-list">
            {/* Header — empty | ACE | DEUCE | (OUT IN) | TOTAL. Sits on
                a single row (no group label since there are only two
                count columns) so the Nassau variant just inserts the
                OUT / IN money columns between counts and TOTAL. */}
            <li
              className={`cg-board-head cg-board-head-aces${
                acesAndDeuces.nassauActive
                  ? " cg-board-head-aces-nassau"
                  : ""
              }`}
              aria-hidden
            >
              <span />
              <span className="cg-board-section">ACE</span>
              <span className="cg-board-section">DEUCE</span>
              {acesAndDeuces.nassauActive && (
                <>
                  <span className="cg-board-section">OUT</span>
                  <span className="cg-board-section">IN</span>
                </>
              )}
              <span className="cg-board-section">TOTAL</span>
            </li>
            {rows.map((r) => {
              const pay = acesAndDeuces.payouts[r.player.id] || {
                points: 0,
                total: 0,
                overall: 0,
                front: 0,
                back: 0,
                aces: { count: 0, holes: [], winnings: 0 },
                deuces: { count: 0, holes: [], winnings: 0 },
              };
              const clsFor = (n: number) =>
                n > 0
                  ? "cg-winnings-pos"
                  : n < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";
              const openDetail = openAcesDetail;
              const isOpen =
                !!openDetail && openDetail.playerId === r.player.id;
              const detail = isOpen ? pay[openDetail!.category] : null;
              const categoryLabel = (c: "aces" | "deuces") =>
                c === "aces" ? "Ace" : "Deuce";
              return (
                <React.Fragment key={r.player.id}>
                  <li
                    className={`cg-board-row cg-board-row-aces${
                      acesAndDeuces.nassauActive
                        ? " cg-board-row-aces-nassau"
                        : ""
                    }`}
                  >
                    <span className="cg-board-name">
                      {r.player.name}
                      {trophy(acesWinners.has(r.player.id))}
                    </span>
                    {(["aces", "deuces"] as const).map((cat) => {
                      const bucket = pay[cat];
                      return (
                        <button
                          key={cat}
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() => toggleAcesDetail(r.player.id, cat)}
                          aria-expanded={
                            isOpen && openDetail?.category === cat
                          }
                          aria-label={`${r.player.name} ${categoryLabel(cat)} holes`}
                        >
                          {bucket.count}
                        </button>
                      );
                    })}
                    {acesAndDeuces.nassauActive && (
                      <>
                        <span
                          className={`cg-board-skin-total ${clsFor(pay.front)}`}
                        >
                          {formatMoney(pay.front, currency)}
                        </span>
                        <span
                          className={`cg-board-skin-total ${clsFor(pay.back)}`}
                        >
                          {formatMoney(pay.back, currency)}
                        </span>
                      </>
                    )}
                    <span
                      className={`cg-board-skin-total ${clsFor(pay.total)}`}
                    >
                      {formatMoney(pay.total, currency)}
                    </span>
                  </li>
                  {detail && (
                    <li className="cg-board-skin-detail">
                      {detail.holes.length === 0
                        ? `No ${categoryLabel(openDetail!.category)} holes yet.`
                        : `${categoryLabel(openDetail!.category)} on hole${
                            detail.holes.length === 1 ? "" : "s"
                          }: ${detail.holes.join(", ")}`}
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
        </section>
        );
      })()}

      {putting && rows.length > 0 && (
        <section className="cg-game-board cg-game-board-putting">
          <h3 className="cg-game-board-title">Putting</h3>
          <ol className="cg-board cg-game-board-list">
            {/* Two-row header. Bet-per-point mode: empty | PUTTS group |
                TOTAL. Nassau mode: empty | PUTTS group | OUT | IN |
                TOTAL — the same category strip plus the two segment
                payout columns. Second header row labels the four putt
                count slots beneath the PUTTS group; remaining cells
                stay empty so the grid lines up with the data rows. */}
            <li
              className={`cg-board-head cg-board-head-putting-cats${
                putting.nassauActive
                  ? " cg-board-head-putting-cats-nassau"
                  : ""
              }`}
              aria-hidden
            >
              <span />
              <span className="cg-board-section cg-board-section-group">
                PUTTS
              </span>
              {putting.nassauActive && (
                <>
                  <span className="cg-board-section">OUT</span>
                  <span className="cg-board-section">IN</span>
                </>
              )}
              <span className="cg-board-section">TOTAL</span>
            </li>
            <li
              className={`cg-board-head cg-board-head-putting-cats cg-board-head-putting-cats-sub${
                putting.nassauActive
                  ? " cg-board-head-putting-cats-nassau"
                  : ""
              }`}
              aria-hidden
            >
              <span />
              <span className="cg-board-section">0</span>
              <span className="cg-board-section">1</span>
              <span className="cg-board-section">3</span>
              <span className="cg-board-section">UD</span>
              {putting.nassauActive && (
                <>
                  <span />
                  <span />
                </>
              )}
              <span />
            </li>
            {rows.map((r) => {
              const pay = putting.payouts[r.player.id] || {
                points: 0,
                total: 0,
                overall: 0,
                front: 0,
                back: 0,
                zeroPutts: { count: 0, holes: [], winnings: 0 },
                onePutts: { count: 0, holes: [], winnings: 0 },
                threePutts: { count: 0, holes: [], winnings: 0 },
                uAndD: { count: 0, holes: [], winnings: 0 },
              };
              const clsFor = (n: number) =>
                n > 0
                  ? "cg-winnings-pos"
                  : n < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";

              // Single category layout for both modes. Nassau adds OUT
              // / IN money cells between the count strip and the TOTAL
              // cell. Tapping any count expands the player's hole list
              // for that category beneath their row.
              const openDetail = openPuttingDetail;
              const isOpen =
                !!openDetail && openDetail.playerId === r.player.id;
              const detail = isOpen ? pay[openDetail!.category] : null;
              const categoryLabel = (
                c: "zeroPutts" | "onePutts" | "threePutts" | "uAndD",
              ) =>
                c === "zeroPutts"
                  ? "0-putt"
                  : c === "onePutts"
                    ? "1-putt"
                    : c === "threePutts"
                      ? "3-putt"
                      : "U&D";

              return (
                <React.Fragment key={r.player.id}>
                  <li
                    className={`cg-board-row cg-board-row-putting-cats${
                      putting.nassauActive
                        ? " cg-board-row-putting-cats-nassau"
                        : ""
                    }`}
                  >
                    <span className="cg-board-name">
                      {r.player.name}
                      {trophy(puttingWinners.has(r.player.id))}
                    </span>
                    {(
                      [
                        "zeroPutts",
                        "onePutts",
                        "threePutts",
                        "uAndD",
                      ] as const
                    ).map((cat) => {
                      const bucket = pay[cat];
                      return (
                        <button
                          key={cat}
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() =>
                            togglePuttingDetail(r.player.id, cat)
                          }
                          aria-expanded={
                            isOpen && openDetail?.category === cat
                          }
                          aria-label={`${r.player.name} ${categoryLabel(cat)} holes`}
                        >
                          {bucket.count}
                        </button>
                      );
                    })}
                    {putting.nassauActive && (
                      <>
                        <span
                          className={`cg-board-skin-total ${clsFor(pay.front)}`}
                        >
                          {formatMoney(pay.front, currency)}
                        </span>
                        <span
                          className={`cg-board-skin-total ${clsFor(pay.back)}`}
                        >
                          {formatMoney(pay.back, currency)}
                        </span>
                      </>
                    )}
                    <span
                      className={`cg-board-skin-total ${clsFor(pay.total)}`}
                    >
                      {formatMoney(pay.total, currency)}
                    </span>
                  </li>
                  {detail && (
                    <li className="cg-board-skin-detail">
                      {detail.holes.length === 0
                        ? `No ${categoryLabel(openDetail!.category)} holes yet.`
                        : `${categoryLabel(openDetail!.category)} on hole${
                            detail.holes.length === 1 ? "" : "s"
                          }: ${detail.holes.join(", ")}`}
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
        </section>
      )}

      {nines && rows.length > 0 && (() => {
        const showGross =
          nines.scoringMode === "gross" || nines.scoringMode === "netGross";
        const showNet =
          nines.scoringMode === "net" || nines.scoringMode === "netGross";
        const nassau = nines.gross.nassauActive || nines.net.nassauActive;
        // Column count: name + (gross pts?) + (net pts?) + (OUT, IN if nassau) + TOTAL.
        const ptsCols = (showGross ? 1 : 0) + (showNet ? 1 : 0);
        // Grid template — name takes the slack, point cells stay tight,
        // OUT/IN/TOTAL match the Putting board's money cell sizing.
        const ptsTemplate = Array(ptsCols).fill("30px").join(" ");
        const nassauTemplate = nassau ? "32px 32px " : "";
        const gridTemplate =
          `minmax(0, 1fr) ${ptsTemplate} ${nassauTemplate}48px`.trim();
        const title =
          "Nines" +
          (nassau ? " — Nassau" : " — Bet per point") +
          (nines.scoringMode === "netGross"
            ? " (Net & Gross)"
            : nines.scoringMode === "gross"
              ? " (Gross)"
              : " (Net)");

        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";

        return (
          <section className="cg-game-board cg-game-board-nines">
            <h3 className="cg-game-board-title">{title}</h3>
            <ol className="cg-board cg-game-board-list">
              {/* Sub-header row — labels point columns + (when Nassau)
                  the OUT/IN money columns, mirroring the Putting panel. */}
              <li
                className="cg-board-head cg-board-head-nines"
                aria-hidden
                style={{ display: "grid", gridTemplateColumns: gridTemplate }}
              >
                <span />
                {showGross && (
                  <span className="cg-board-section">G</span>
                )}
                {showNet && (
                  <span className="cg-board-section">N</span>
                )}
                {nassau && (
                  <>
                    <span className="cg-board-section">OUT</span>
                    <span className="cg-board-section">IN</span>
                  </>
                )}
                <span className="cg-board-section">TOTAL</span>
              </li>
              {rows.map((r) => {
                const g = nines.gross.payouts[r.player.id];
                const n = nines.net.payouts[r.player.id];
                const total =
                  (g?.total || 0) + (n?.total || 0);
                // Combined OUT/IN across the two sections in Nassau
                // mode — surfaces a single segment cash figure.
                const front = (g?.front || 0) + (n?.front || 0);
                const back = (g?.back || 0) + (n?.back || 0);
                const open = openNinesDetail;
                const isDetailOpen =
                  !!open && open.playerId === r.player.id;
                const detailSection =
                  isDetailOpen && open
                    ? open.section === "gross"
                      ? g
                      : n
                    : null;
                return (
                  <React.Fragment key={r.player.id}>
                    <li
                      className="cg-board-row cg-board-row-nines"
                      style={{
                        display: "grid",
                        gridTemplateColumns: gridTemplate,
                      }}
                    >
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(ninesWinners.has(r.player.id))}
                      </span>
                      {showGross && (
                        <button
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() =>
                            toggleNinesDetail(r.player.id, "gross")
                          }
                          aria-expanded={
                            isDetailOpen && open?.section === "gross"
                          }
                          aria-label={`${r.player.name} gross Nines points`}
                        >
                          {g?.points || 0}
                        </button>
                      )}
                      {showNet && (
                        <button
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() =>
                            toggleNinesDetail(r.player.id, "net")
                          }
                          aria-expanded={
                            isDetailOpen && open?.section === "net"
                          }
                          aria-label={`${r.player.name} net Nines points`}
                        >
                          {n?.points || 0}
                        </button>
                      )}
                      {nassau && (
                        <>
                          <span
                            className={`cg-board-skin-total ${clsFor(front)}`}
                          >
                            {formatMoney(front, currency)}
                          </span>
                          <span
                            className={`cg-board-skin-total ${clsFor(back)}`}
                          >
                            {formatMoney(back, currency)}
                          </span>
                        </>
                      )}
                      <span
                        className={`cg-board-skin-total ${clsFor(total)}`}
                      >
                        {formatMoney(total, currency)}
                      </span>
                    </li>
                    {detailSection && (
                      <li className="cg-board-skin-detail cg-nines-detail">
                        {/* Every player scores on every hole, so a hole list
                            (1,2,…,18) is just noise. Show the front/back point
                            split instead. */}
                        <span className="cg-nines-detail-line">
                          OUT: {detailSection.frontPoints} points
                        </span>
                        <span className="cg-nines-detail-line">
                          IN: {detailSection.backPoints} points
                        </span>
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {wolf && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";
        const gridTemplate = "minmax(0, 1fr) 56px";
        return (
          <section className="cg-game-board cg-game-board-wolf">
            <h3 className="cg-game-board-title">
              Wolf — {wolf.scoringMode === "net" ? "Net" : "Gross"}
            </h3>
            <ol className="cg-board cg-game-board-list">
              <li
                className="cg-board-head"
                aria-hidden
                style={{ display: "grid", gridTemplateColumns: gridTemplate }}
              >
                <span />
                <span className="cg-board-section">TOTAL</span>
              </li>
              {rows.map((r) => {
                const pay = wolf.payouts[r.player.id] || {
                  total: 0,
                  details: [],
                };
                const isOpen = openWolfDetail === r.player.id;
                return (
                  <React.Fragment key={r.player.id}>
                    <li
                      className="cg-board-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: gridTemplate,
                      }}
                    >
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(wolfWinners.has(r.player.id))}
                      </span>
                      <button
                        type="button"
                        className={`cg-board-skin-total ${clsFor(pay.total)}`}
                        onClick={() => toggleWolfDetail(r.player.id)}
                        aria-expanded={isOpen}
                        aria-label={`${r.player.name} Wolf detail`}
                      >
                        {formatMoney(pay.total, currency)}
                      </button>
                    </li>
                    {isOpen && (
                      <li className="cg-board-skin-detail">
                        {pay.details.length === 0
                          ? "No settled holes yet."
                          : pay.details.map((d, i) => (
                              <span
                                key={i}
                                className="cg-nines-detail-line"
                              >
                                Hole {d.hole}: {formatMoney(d.delta, currency)}{" "}
                                — {d.note}
                              </span>
                            ))}
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {rabbit && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";
        const gridTemplate = "minmax(0, 1fr) 56px";
        const nameFor = (id: string | null) =>
          id ? players.find((p) => p.id === id)?.name || "—" : "—";
        const segLine = (
          label: string,
          pot: number,
          winner: string | null,
          settled: boolean,
        ) =>
          `${label} pot ${formatMoney(pot, currency)}: ${
            winner ? nameFor(winner) : "no holder"
          }${settled ? "" : " (in play)"}`;
        return (
          <section className="cg-game-board cg-game-board-rabbit">
            <h3 className="cg-game-board-title">
              Rabbit — {rabbit.scoringMode === "net" ? "Net" : "Gross"}
            </h3>
            <ol className="cg-board cg-game-board-list">
              <li
                className="cg-board-head"
                aria-hidden
                style={{ display: "grid", gridTemplateColumns: gridTemplate }}
              >
                <span />
                <span className="cg-board-section">TOTAL</span>
              </li>
              {rows.map((r) => {
                const total = rabbit.payouts[r.player.id]?.total || 0;
                const holdsRabbit =
                  (!rabbit.frontSettled &&
                    rabbit.frontWinner === r.player.id) ||
                  (rabbit.frontSettled &&
                    !rabbit.backSettled &&
                    rabbit.backWinner === r.player.id);
                return (
                  <li
                    key={r.player.id}
                    className="cg-board-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridTemplate,
                    }}
                  >
                    <span className="cg-board-name">
                      {r.player.name}
                      {holdsRabbit && (
                        <span
                          className="cg-board-trophy"
                          role="img"
                          aria-label="Holds the Rabbit"
                        >
                          {" "}
                          🐰
                        </span>
                      )}
                      {trophy(rabbitWinners.has(r.player.id))}
                    </span>
                    <span className={`cg-board-skin-total ${clsFor(total)}`}>
                      {formatMoney(total, currency)}
                    </span>
                  </li>
                );
              })}
            </ol>
            <p className="cg-board-skin-detail cg-rabbit-segments">
              <span className="cg-nines-detail-line">
                {segLine(
                  "Front 9",
                  rabbit.frontPot,
                  rabbit.frontWinner,
                  rabbit.frontSettled,
                )}
              </span>
              <span className="cg-nines-detail-line">
                {segLine(
                  "Back 9",
                  rabbit.backPot,
                  rabbit.backWinner,
                  rabbit.backSettled,
                )}
              </span>
            </p>
          </section>
        );
      })()}

      {bbb && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";
        const catLabel: Record<BbbCategory, string> = {
          bingo: "Bingo",
          bango: "Bango",
          bongo: "Bongo",
        };
        const cats: BbbCategory[] = ["bingo", "bango", "bongo"];
        return (
          <section className="cg-game-board cg-game-board-bbb">
            <h3 className="cg-game-board-title">Bingo Bango Bongo</h3>
            <ol className="cg-board cg-game-board-list">
              <li className="cg-board-head cg-board-head-bbb" aria-hidden>
                <span />
                <span className="cg-board-section">BIN</span>
                <span className="cg-board-section">BAN</span>
                <span className="cg-board-section">BON</span>
                <span className="cg-board-section">TOTAL</span>
              </li>
              {rows.map((r) => {
                const pay = bbb.payouts[r.player.id];
                if (!pay) return null;
                const open = openBbbDetail;
                const isRowOpen = !!open && open.playerId === r.player.id;
                const detailBucket =
                  isRowOpen && open ? pay[open.category] : null;
                return (
                  <React.Fragment key={r.player.id}>
                    <li className="cg-board-row cg-board-row-bbb">
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(bbbWinners.has(r.player.id))}
                      </span>
                      {cats.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() => toggleBbbDetail(r.player.id, cat)}
                          aria-expanded={isRowOpen && open?.category === cat}
                          aria-label={`${r.player.name} ${catLabel[cat]} holes`}
                        >
                          {pay[cat].count}
                        </button>
                      ))}
                      <span
                        className={`cg-board-skin-total ${clsFor(pay.total)}`}
                      >
                        {formatMoney(pay.total, currency)}
                      </span>
                    </li>
                    {detailBucket && open && (
                      <li className="cg-board-skin-detail">
                        {detailBucket.holes.length === 0
                          ? `No ${catLabel[open.category]} yet.`
                          : `${catLabel[open.category]} on hole${
                              detailBucket.holes.length === 1 ? "" : "s"
                            }: ${detailBucket.holes.join(", ")}`}
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {bounceBack && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";
        const catLabel: Record<BounceBackCategory, string> = {
          bbPar: "BB Par",
          bbBird: "BB Bird",
          bbDbl: "BB Dbl",
        };
        // Spelled-out labels for the expanded hole list.
        const catDetailLabel: Record<BounceBackCategory, string> = {
          bbPar: "DBL followed by Par",
          bbBird: "DBL followed by Birdie",
          bbDbl: "DBL followed by DBL",
        };
        const cats: BounceBackCategory[] = ["bbPar", "bbBird", "bbDbl"];
        return (
          <section className="cg-game-board cg-game-board-bounceback">
            <h3 className="cg-game-board-title">Bounce Back</h3>
            <ol className="cg-board cg-game-board-list">
              <li className="cg-board-head cg-board-head-bbk" aria-hidden>
                <span />
                <span className="cg-board-section">BB Par</span>
                <span className="cg-board-section">BB Bird</span>
                <span className="cg-board-section">BB Dbl</span>
                <span className="cg-board-section">TOTAL</span>
              </li>
              {rows.map((r) => {
                const pay = bounceBack.payouts[r.player.id];
                if (!pay) return null;
                const open = openBounceBackDetail;
                const isRowOpen = !!open && open.playerId === r.player.id;
                const detailBucket =
                  isRowOpen && open ? pay[open.category] : null;
                return (
                  <React.Fragment key={r.player.id}>
                    <li className="cg-board-row cg-board-row-bbk">
                      <span className="cg-board-name">
                        {r.player.name}
                        {trophy(bounceBackWinners.has(r.player.id))}
                      </span>
                      {cats.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          className="cg-board-skin-count"
                          onClick={() =>
                            toggleBounceBackDetail(r.player.id, cat)
                          }
                          aria-expanded={isRowOpen && open?.category === cat}
                          aria-label={`${r.player.name} ${catLabel[cat]} holes`}
                        >
                          {pay[cat].count}
                        </button>
                      ))}
                      <span
                        className={`cg-board-skin-total ${clsFor(pay.total)}`}
                      >
                        {formatMoney(pay.total, currency)}
                      </span>
                    </li>
                    {detailBucket && open && (
                      <li className="cg-board-skin-detail">
                        {detailBucket.holes.length === 0
                          ? `No ${catDetailLabel[open.category]} yet.`
                          : `${catDetailLabel[open.category]} on hole${
                              detailBucket.holes.length === 1 ? "" : "s"
                            }: ${detailBucket.holes.join(", ")}`}
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {matchPlay && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";
        const nassau = matchPlay.nassauActive;
        const team = matchPlay.teamMode;
        const dual = matchPlay.dualMode;
        const gridTemplate = "minmax(0, 1fr) 50px 34px 48px 48px 56px";
        // Overall match status from a player's side perspective.
        const statusStr = (diff: number) =>
          diff > 0 ? `${diff} Up` : diff < 0 ? `${-diff} Dn` : "AS";
        const nameById = new Map(players.map((p) => [p.id, p.name] as const));

        const headRow = (
          <li
            className="cg-board-head"
            aria-hidden
            style={{ display: "grid", gridTemplateColumns: gridTemplate }}
          >
            <span />
            <span className="cg-board-section">SCORE</span>
            <span className="cg-board-section">THRU</span>
            <span className="cg-board-section">OUT</span>
            <span className="cg-board-section">IN</span>
            <span className="cg-board-section">TOTAL</span>
          </li>
        );

        const renderRow = (playerId: string, showTrophy: boolean) => {
          const pay = matchPlay.payouts[playerId];
          if (!pay) return null;
          // In team mode the up/down status sits on the first player of
          // each side only; in 1-v-1 (incl. dual) every row shows it.
          const showStatus = !team || pay.firstOnSide;
          return (
            <li
              key={playerId}
              className="cg-board-row"
              style={{ display: "grid", gridTemplateColumns: gridTemplate }}
            >
              <span className="cg-board-name">
                {team && pay.team != null && (
                  <span
                    className={`cg-board-team-dot cg-board-team-dot-team${
                      pay.team + 1
                    }`}
                    aria-hidden
                  />
                )}
                {nameById.get(playerId) ?? ""}
                {trophy(showTrophy)}
              </span>
              <span className="cg-board-matchplay-status">
                {showStatus ? statusStr(pay.statusDiff) : ""}
              </span>
              <span className="cg-board-matchplay-thru">
                {pay.thru || ""}
              </span>
              <span className={`cg-board-skin-total ${clsFor(pay.front)}`}>
                {nassau ? formatMoney(pay.front, currency) : "—"}
              </span>
              <span className={`cg-board-skin-total ${clsFor(pay.back)}`}>
                {nassau ? formatMoney(pay.back, currency) : "—"}
              </span>
              <span className={`cg-board-skin-total ${clsFor(pay.total)}`}>
                {formatMoney(pay.total, currency)}
              </span>
            </li>
          );
        };

        // Dual mode — two independent 1-v-1 matches. Render one standings
        // panel per match, each with its own winner trophy (whoever holds
        // the positive total in that pairing).
        if (dual) {
          return (
            <section className="cg-game-board cg-game-board-matchplay">
              <h3 className="cg-game-board-title">Match Play — Individual</h3>
              {matchPlay.matches.map((m) => {
                const [id1, id2] = m.playerIds;
                const t1 = matchPlay.payouts[id1]?.total || 0;
                const t2 = matchPlay.payouts[id2]?.total || 0;
                const winner =
                  t1 > t2 && t1 > 0 ? id1 : t2 > t1 && t2 > 0 ? id2 : null;
                return (
                  <div key={m.index} className="cg-board-matchplay-group">
                    <h4 className="cg-board-matchplay-subtitle">
                      Match {m.index + 1}
                    </h4>
                    <ol className="cg-board cg-game-board-list">
                      {headRow}
                      {m.playerIds.map((pid) => renderRow(pid, winner === pid))}
                    </ol>
                  </div>
                );
              })}
            </section>
          );
        }

        return (
          <section className="cg-game-board cg-game-board-matchplay">
            <h3 className="cg-game-board-title">Match Play</h3>
            <ol className="cg-board cg-game-board-list">
              {headRow}
              {rows.map((r) =>
                renderRow(r.player.id, matchPlayWinners.has(r.player.id)),
              )}
            </ol>
          </section>
        );
      })()}

      {hiLo && rows.length > 0 && (() => {
        const active = games?.find((g) => g.id === "hiLo" && g.enabled);
        const mode =
          active?.options?.scoringMode === "gross" ? "Gross" : "Net";
        return (
        <section className="cg-game-board cg-game-board-hilo">
          <h3 className="cg-game-board-title">Hi-Lo — {mode}</h3>
          <ol className="cg-board cg-game-board-list cg-board-grid-hilo">
            <li
              className="cg-board-head cg-board-head-hilo"
              aria-hidden
            >
              <span />
              <span />
              <span />
              <span className="cg-board-section">LOW</span>
              <span className="cg-board-section">HIGH</span>
              <span className="cg-board-section">TOTAL</span>
            </li>
            {rows.map((r) => {
              const pay = hiLo.payouts[r.player.id] || {
                lowPoints: 0,
                highPoints: 0,
                teamPoints: 0,
                total: 0,
              };
              const clsFor = (n: number) =>
                n > 0
                  ? "cg-winnings-pos"
                  : n < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";
              return (
                <li
                  key={r.player.id}
                  className="cg-board-row cg-board-row-hilo"
                >
                  <span className="cg-board-name">
                    {r.player.name}
                    {trophy(hiLoWinners.has(r.player.id))}
                  </span>
                  <span className="cg-board-team-dot-cell">
                    {teamDot(r.player.id, hiLoTeams)}
                  </span>
                  <span aria-hidden />
                  <span className="cg-board-skin-count cg-board-hilo-pts">
                    {formatPoints(pay.lowPoints)}
                  </span>
                  <span className="cg-board-skin-count cg-board-hilo-pts">
                    {formatPoints(pay.highPoints)}
                  </span>
                  <span
                    className={`cg-board-skin-total ${clsFor(pay.total)}`}
                  >
                    {formatMoney(pay.total, currency)}
                  </span>
                </li>
              );
            })}
          </ol>
          {hiLo.presses.length > 0 && (
            <ul className="cg-board-hilo-presses">
              {hiLo.presses.map((pr) => (
                <li key={pr.index} className="cg-board-hilo-press">
                  <span className="cg-board-hilo-press-label">
                    Press {pr.index} — started Hole {pr.startHole}
                  </span>
                  <span className="cg-board-hilo-press-score">
                    A {formatPoints(pr.teamAPoints)} · B {formatPoints(pr.teamBPoints)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        );
      })()}

      {vegas && rows.length > 0 && (
        <section className="cg-game-board cg-game-board-vegas">
          <h3 className="cg-game-board-title">
            Vegas — {vegas.scoringMode === "gross" ? "Gross" : "Net"}
          </h3>
          <ol className="cg-board cg-game-board-list cg-board-grid-vegas">
            <li
              className="cg-board-head cg-board-head-vegas"
              aria-hidden
            >
              <span />
              <span />
              <span />
              <span className="cg-board-section">DIFF</span>
              <span className="cg-board-section">TOTAL</span>
            </li>
            {rows.map((r) => {
              const pay = vegas.payouts[r.player.id];
              if (!pay) return null;
              const clsFor = (n: number) =>
                n > 0
                  ? "cg-winnings-pos"
                  : n < 0
                    ? "cg-winnings-neg"
                    : "cg-winnings-zero";
              return (
                <li
                  key={r.player.id}
                  className="cg-board-row cg-board-row-vegas"
                >
                  <span className="cg-board-name">
                    {r.player.name}
                    {trophy(vegasWinners.has(r.player.id))}
                  </span>
                  <span className="cg-board-team-dot-cell">
                    {teamDot(r.player.id, vegasTeams)}
                  </span>
                  <span aria-hidden />
                  <span className={`cg-board-vegas-diff ${clsFor(pay.teamDelta)}`}>
                    {pay.teamDelta > 0 ? `+${pay.teamDelta}` : pay.teamDelta}
                  </span>
                  <span className={`cg-board-skin-total ${clsFor(pay.total)}`}>
                    {formatMoney(pay.total, currency)}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {hollywood && rows.length > 0 && (() => {
        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";

        const subGameLabel =
          hollywood.subGame === "stableford"
            ? "Stableford"
            : hollywood.subGame === "hiLo"
              ? "Hi-Lo"
              : hollywood.subGame === "vegas"
                ? "Vegas"
                : "Stroke Play";

        // Segment column labels — "10–15" etc. With a shotgun start these
        // wrap (e.g. "16–3"); the runner hands us the first/last hole of
        // each 6-hole leg in play order.
        const segRangeLabel = (i: number) => {
          const r = hollywood.segmentRanges?.[i];
          return r ? `${r[0]}–${r[1]}` : "";
        };

        return (
          <section className="cg-game-board cg-game-board-hollywood">
            <h3 className="cg-game-board-title">
              Hollywood — {subGameLabel}
            </h3>
            <ol className="cg-board cg-game-board-list">
              {/* Header row labels the three 6-hole segments + total.
                  Columns line up with the data rows: name (slack) →
                  three dots → three leg cells → total. TOTAL is
                  right-aligned in its cell so it reads as the
                  trailing summary rather than another leg label. */}
              <li
                className="cg-board-head cg-board-head-hollywood"
                aria-hidden
              >
                <span />
                <span className="cg-board-hollywood-dots-head" />
                <span className="cg-board-section">{segRangeLabel(0)}</span>
                <span className="cg-board-section">{segRangeLabel(1)}</span>
                <span className="cg-board-section">{segRangeLabel(2)}</span>
                <span className="cg-board-section cg-board-hollywood-total-head">
                  TOTAL
                </span>
              </li>
              {rows.map((r) => {
                const pay = hollywood.payouts[r.player.id];
                if (!pay) return null;
                return (
                  <li
                    key={r.player.id}
                    className="cg-board-row cg-board-row-hollywood"
                  >
                    <span className="cg-board-name">{r.player.name}</span>
                    {/* Three-dot team strip — green = teamA, gold =
                        teamB, one per 6-hole segment. No trophy /
                        banner / shuffle button: the dots carry the
                        team identity on their own. */}
                    <span
                      className="cg-board-hollywood-dots"
                      aria-label="Hollywood team per segment"
                    >
                      {pay.segments.map((seg, i) => (
                        <span
                          key={`hwd-${r.player.id}-${i}`}
                          className={`cg-board-hollywood-dot cg-board-hollywood-dot-${
                            seg.team === "teamA" ? "a" : "b"
                          }`}
                          aria-hidden
                        />
                      ))}
                    </span>
                    {pay.segments.map((seg, i) => (
                      <span
                        key={`hws-${r.player.id}-${i}`}
                        className={`cg-board-hollywood-money ${clsFor(seg.net)}`}
                      >
                        {seg.settled ? formatMoney(seg.net, currency) : "—"}
                      </span>
                    ))}
                    <span
                      className={`cg-board-skin-total ${clsFor(pay.total)}`}
                    >
                      {formatMoney(pay.total, currency)}
                    </span>
                  </li>
                );
              })}
            </ol>

            {/* Per-segment press list — Hi-Lo subgame only. Reads off
                any one player's payout (segment data is identical for
                both teammates within a segment). Same "Press X —
                started Hole Y · A pts · B pts" format as the
                standalone Hi-Lo panel so the user sees a consistent
                press summary regardless of which game's panel they're
                looking at. */}
            {hollywood.subGame === "hiLo" && rows.length > 0 && (() => {
              const segLabels: [string, string, string] = [
                `Holes ${segRangeLabel(0)}`,
                `Holes ${segRangeLabel(1)}`,
                `Holes ${segRangeLabel(2)}`,
              ];
              const firstPay = hollywood.payouts[rows[0].player.id];
              if (!firstPay) return null;
              const segmentsWithPresses = firstPay.segments
                .map((seg, i) => ({ seg, i }))
                .filter((x) => (x.seg.presses?.length ?? 0) > 0);
              if (segmentsWithPresses.length === 0) return null;
              return (
                <div className="cg-board-hollywood-presses">
                  {segmentsWithPresses.map(({ seg, i }) => (
                    <div
                      key={`hwp-seg-${i}`}
                      className="cg-board-hollywood-press-segment"
                    >
                      <div className="cg-board-hollywood-press-segment-label">
                        {segLabels[i]}
                      </div>
                      <ul className="cg-board-hilo-presses">
                        {(seg.presses ?? []).map((pr) => (
                          <li
                            key={`hwp-${i}-${pr.index}`}
                            className="cg-board-hilo-press"
                          >
                            <span className="cg-board-hilo-press-label">
                              Press {pr.index} — started Hole {pr.startHole}
                            </span>
                            <span className="cg-board-hilo-press-score">
                              A {formatPoints(pr.teamAPoints)} · B {formatPoints(pr.teamBPoints)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}
          </section>
        );
      })()}

      {rows.length > 0 && (() => {
        // Per-player breakdown across every game that produced a result.
        // Each entry's `amount` is signed cash from that game's payout
        // (positive = winnings, negative = owed). Skins is special: the
        // per-section `.winnings` is gross of the ante, so we subtract the
        // player's even share of the combined stake to produce a net position
        // — matching the Skins board's own TOTAL column. `skinsStake` is 0 when
        // the entry fee was paid up front.
        const skinsStakePerPlayer = skinsStake;

        // Whether a game's "Entry Fee Paid" option is on. Fee-paid games pay
        // GROSS winnings (the pot/ante was collected up front, so the money is
        // owed to the player no matter what) — they're totalled separately
        // from the settle-up games whose bet is netted out.
        const feePaidFor = (gameId: string): boolean => {
          const active = games?.find((g) => g.id === gameId && g.enabled);
          return active?.options?.entryFeePaid === true;
        };

        const breakdownFor = (
          id: string,
        ): { label: string; amount: number; feePaid: boolean }[] => {
          const out: { label: string; amount: number; feePaid: boolean }[] = [];
          if (strokePlay) {
            out.push({
              label: "Stroke Play",
              amount: strokePlay.payouts[id]?.total || 0,
              feePaid: feePaidFor("strokePlay"),
            });
          }
          if (stableford?.hasAnyBet && !onTournamentBoard) {
            out.push({
              label: "Stableford",
              amount: stableford.payouts[id]?.total || 0,
              feePaid: feePaidFor("stableford"),
            });
          }
          if (skins?.hasAnyBet) {
            const g = skins.gross.payouts[id]?.winnings || 0;
            const n = skins.net.payouts[id]?.winnings || 0;
            out.push({
              label: "Skins",
              amount: g + n - skinsStakePerPlayer,
              feePaid: feePaidFor("skins"),
            });
          }
          // Team games (tournament board only) — each player's share.
          if (allianceBets) {
            out.push({
              label: "Alliance",
              amount: allianceBets.payouts[id] || 0,
              feePaid: feePaidFor("alliance"),
            });
          }
          if (chaChaChaBets) {
            out.push({
              label: "Cha Cha Cha",
              amount: chaChaChaBets.payouts[id] || 0,
              feePaid: feePaidFor("chaChaCha"),
            });
          }
          if (stablefordTeamBets) {
            out.push({
              label: "Stableford",
              amount: stablefordTeamBets.payouts[id] || 0,
              feePaid: feePaidFor("stableford"),
            });
          }
          if (oosler) {
            out.push({
              label: "Oosler / Woosler",
              amount: oosler.payouts[id]?.points || 0,
              feePaid: false,
            });
          }
          if (acesAndDeuces) {
            out.push({
              label: "Aces & Deuces",
              amount: acesAndDeuces.payouts[id]?.total || 0,
              feePaid: false,
            });
          }
          if (putting) {
            out.push({
              label: "Putting",
              amount: putting.payouts[id]?.total || 0,
              feePaid: false,
            });
          }
          if (nines) {
            out.push({
              label: "Nines",
              amount:
                (nines.gross.payouts[id]?.total || 0) +
                (nines.net.payouts[id]?.total || 0),
              feePaid: false,
            });
          }
          if (hiLo) {
            out.push({
              label: "Hi-Lo",
              amount: hiLo.payouts[id]?.total || 0,
              feePaid: false,
            });
          }
          if (vegas) {
            out.push({
              label: "Vegas",
              amount: vegas.payouts[id]?.total || 0,
              feePaid: false,
            });
          }
          if (hollywood?.hasAnyBet) {
            out.push({
              label: "Hollywood",
              amount: hollywood.payouts[id]?.total || 0,
              feePaid: false,
            });
          }
          return out;
        };

        // Nothing to total if no game produced a result.
        if (breakdownFor(rows[0].player.id).length === 0) return null;

        const settleTotalFor = (id: string) =>
          breakdownFor(id)
            .filter((e) => !e.feePaid)
            .reduce((sum, e) => sum + e.amount, 0);
        const feePaidTotalFor = (id: string) =>
          breakdownFor(id)
            .filter((e) => e.feePaid)
            .reduce((sum, e) => sum + e.amount, 0);
        const totalFor = (id: string) =>
          settleTotalFor(id) + feePaidTotalFor(id);

        // Two columns only when there's actually a fee-paid game in play
        // (otherwise the second column would be all zeros). The split is a
        // tournament concept, so the local/group board stays single-column.
        const anyFeePaid = breakdownFor(rows[0].player.id).some(
          (e) => e.feePaid,
        );
        const twoCol = onTournamentBoard && anyFeePaid;

        const totals = rows.map((r) => ({
          id: r.player.id,
          total: totalFor(r.player.id),
        }));

        // Trophy goes to top net position(s) — ties surface a trophy on
        // every tied player. No trophy when nobody is up money.
        const max = Math.max(...totals.map((t) => t.total));
        const overallWinners = new Set<string>(
          max > 0 ? totals.filter((t) => t.total === max).map((t) => t.id) : [],
        );

        const clsFor = (n: number) =>
          n > 0
            ? "cg-winnings-pos"
            : n < 0
              ? "cg-winnings-neg"
              : "cg-winnings-zero";

        return (
          <section className="cg-game-board cg-game-board-total">
            <h3 className="cg-game-board-title">Round Total</h3>
            <ol className="cg-board cg-game-board-list">
              {twoCol && (
                <li
                  className="cg-board-head cg-board-head-total-split"
                  aria-hidden
                >
                  <span />
                  <span className="cg-board-section">SETTLE</span>
                  <span className="cg-board-section">FEE PAID</span>
                </li>
              )}
              {rows.map((r) => {
                const breakdown = breakdownFor(r.player.id);
                const settle = settleTotalFor(r.player.id);
                const feePaidAmt = feePaidTotalFor(r.player.id);
                const total = totalFor(r.player.id);
                const isOpen = openTotalDetail === r.player.id;
                return (
                  <React.Fragment key={r.player.id}>
                    <li
                      className={`cg-board-row ${
                        twoCol
                          ? "cg-board-row-total-split"
                          : "cg-board-row-payout cg-board-row-total"
                      }`}
                    >
                      <button
                        type="button"
                        className="cg-board-name cg-board-total-name"
                        onClick={() => toggleTotalDetail(r.player.id)}
                        aria-expanded={isOpen}
                        aria-label={`${r.player.name} round total breakdown`}
                      >
                        {r.player.name}
                        {trophy(overallWinners.has(r.player.id))}
                      </button>
                      {twoCol ? (
                        <>
                          <span
                            className={`cg-board-winnings ${clsFor(settle)}`}
                          >
                            {formatMoney(settle, currency)}
                          </span>
                          <span
                            className={`cg-board-winnings ${clsFor(feePaidAmt)}`}
                          >
                            {formatMoney(feePaidAmt, currency)}
                          </span>
                        </>
                      ) : (
                        <span className={`cg-board-winnings ${clsFor(total)}`}>
                          {formatMoney(total, currency)}
                        </span>
                      )}
                    </li>
                    {isOpen && (
                      <li className="cg-board-total-detail">
                        {breakdown.map((e, i) => (
                          <div
                            key={`${e.label}-${i}`}
                            className="cg-board-total-detail-row"
                          >
                            <span className="cg-board-total-detail-label">
                              {e.label}
                              {twoCol && e.feePaid ? " (fee paid)" : ""}
                            </span>
                            <span
                              className={`cg-board-total-detail-amount ${clsFor(
                                e.amount,
                              )}`}
                            >
                              {formatMoney(e.amount, currency)}
                            </span>
                          </div>
                        ))}
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </section>
        );
      })()}

      {onEndRound && (
        <div className="cg-board-end">
          <button
            type="button"
            className="cg-btn cg-btn-end-round"
            onClick={() => setConfirmingEnd(true)}
          >
            End Round
          </button>
        </div>
      )}

      {confirmingEnd && onEndRound && (
        <ConfirmModal
          title="End round?"
          message="This round will be saved to History and cleared from the scorecard. This can't be undone."
          confirmLabel="End Round"
          variant="danger"
          onConfirm={() => {
            setConfirmingEnd(false);
            onEndRound();
          }}
          onCancel={() => setConfirmingEnd(false)}
        />
      )}
    </ModalShell>
  );
}

// Render a signed money amount. Whole-unit bets render without
// decimals; anything fractional (e.g. an odd-numbered pot split) shows
// two decimal places so the math is auditable. Currency symbol comes
// from the owner profile (loadOwner().betCurrency); falls back to "$"
// for legacy owners with no preference saved. Pass signed=false for
// gross-only amounts (Skins winnings) so the +/- prefix is dropped.
function formatMoney(
  n: number,
  symbol: string = "$",
  signed: boolean = true,
): string {
  if (n === 0) return `${symbol}0`;
  const sign = n > 0 ? "+" : "-";
  const abs = Math.abs(n);
  const body =
    Number.isInteger(abs)
      ? `${symbol}${abs}`
      : `${symbol}${abs.toFixed(2)}`;
  return signed ? `${sign}${body}` : body;
}

// Render a Hi-Lo point total. Tied holes split into 0.5/0.5 so the
// running total can be fractional; integer values render bare and
// halves render with a single decimal so the column stays narrow.
function formatPoints(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// "Stableford — Standard" / "Stableford — Modified (PGA)" — Stableford
// is always net now (gross-Stableford option was removed), so only the
// points scale needs to surface in the betting sub-board title.
function stablefordLabel(opts: StablefordOptions): string {
  const scale =
    opts.scoringScale === "modifiedPga"
      ? "Modified (PGA)"
      : opts.scoringScale === "modified"
        ? "Modified"
        : "Standard";
  return `Stableford — ${scale}`;
}
